/**
 * Every tenant's break-glass audit (D20, `GET /vendor/audit`): offline PIN
 * and emergency-account sign-ins the nodes recorded while cut off, newest
 * first, a page at a time. A failure outcome is flagged.
 */
import type { AuditEntry } from '@acp/api-contract/offlineAuth.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type VendorAuditTableProps = {
  readonly entries: readonly AuditEntry[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly onMore: () => void;
};

export function VendorAuditTable({ entries, loading, error, hasMore, onMore }: VendorAuditTableProps) {
  return (
    <div className="grid gap-3" data-testid="vendor-audit">
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          Could not load the audit: {error}
        </p>
      )}
      {!loading && error === null && entries.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No break-glass sign-ins recorded.</p> : null}
      {entries.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={`${e.tenantId}/${e.auditId}`} data-testid="audit-row">
                <TableCell className="whitespace-nowrap text-sm">{e.at.slice(0, 19).replace('T', ' ')}</TableCell>
                <TableCell className="font-mono text-xs">{e.tenantId}</TableCell>
                <TableCell className="text-sm">{e.siteId}</TableCell>
                <TableCell className="text-sm">{e.kind}</TableCell>
                <TableCell className="text-sm">{e.account}</TableCell>
                <TableCell>
                  <Badge variant={e.outcome === 'success' ? 'secondary' : 'warning'}>{e.outcome}</Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{e.sourceIp ?? '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {hasMore && !loading ? (
        <Button variant="outline" size="sm" className="w-fit" onClick={onMore} data-testid="audit-more">
          Load more
        </Button>
      ) : null}
    </div>
  );
}
